import React, { FC, useCallback, useEffect, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { CookieBanner } from 'components/cookie-banner';
import { useHasAcceptedCookies } from 'hooks/use-has-accepted-cookies';
import { useHasIgnoredCookies } from 'hooks/use-has-ignored-cookies';
import useRoutes from 'hooks/use-routes';
import { useAuthContext } from 'state/auth';
import { useBranchContext } from 'state/branch';
import { useBrazeContext } from 'state/braze';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMParticleContext } from 'state/mParticle';
import LocalStorage, { COOKIE_VERSION, StorageKeys } from 'utils/local-storage';
import LogRocketHelper from 'utils/logrocket';

const messages = defineMessages({
  info: {
    id: 'cookiePolicy',
    defaultMessage:
      'By clicking "{acceptCookies}", you agree to the storing of cookies on your device to enhance site navigation, analyze site usage, and assist in our marketing efforts.',
    description: 'Information regarding cookie policy.'
  },
  button: {
    id: 'acceptAllCookies',
    defaultMessage: 'Accept All Cookies',
    description:
      'User accepts cookies in compliance with GDPR / CCPA or similar policies when clicked.'
  },
  cookieNotice: {
    id: 'privacyPolicy',
    defaultMessage: 'Privacy Policy',
    description: 'Text for cookie notice policy link.'
  }
});

const CookieBannerContainer: FC = () => {
  const { formatMessage } = useIntl();
  const { user } = useAuthContext();
  const { init: initMParticle } = useMParticleContext();
  const { initBraze } = useBrazeContext();
  const { initBranch } = useBranchContext();
  const enableLogRocket = useFlag(LaunchDarklyFlag.ENABLE_LOGROCKET);
  const enableCookieVersioning = useFlag(LaunchDarklyFlag.ENABLE_COOKIE_VERSIONING);
  const [showBanner, setShowBanner] = useState(false);
  const { getLocalizedRouteForPath } = useRoutes();

  const hasAcceptedCookies = useHasAcceptedCookies();
  const hasIgnoredCookies = useHasIgnoredCookies();
  const lrInstance = LogRocketHelper.getInstance().logrocket;

  useEffect(() => {
    setShowBanner(!hasAcceptedCookies && !hasIgnoredCookies);
  }, [hasAcceptedCookies, hasIgnoredCookies, user]);

  const initServicesWithCookies = useCallback(() => {
    if (!window.mParticle) {
      initMParticle();
    }
    if (enableLogRocket && !lrInstance) {
      LogRocketHelper.init();
    }
    if (!window.rbiAppboy) {
      initBraze();
    }
    if (!window.branch) {
      initBranch();
    }
  }, [enableLogRocket, initBranch, initBraze, initMParticle, lrInstance]);

  const handleAccept = useCallback(() => {
    LocalStorage.setItem(StorageKeys.LAST_TIME_COOKIES_ACCEPTED, Date.now());
    if (enableCookieVersioning) {
      LocalStorage.setItem(StorageKeys.LAST_COOKIE_VERSION, COOKIE_VERSION);
    }
    initServicesWithCookies();
    setShowBanner(false);
  }, [enableCookieVersioning, initServicesWithCookies]);

  const handleClose = useCallback(() => {
    LocalStorage.setItem(StorageKeys.LAST_TIME_COOKIES_IGNORED, Date.now());
    setShowBanner(false);
  }, []);

  const link = {
    text: formatMessage(messages.cookieNotice),
    href: getLocalizedRouteForPath('privacy-policy')
  };

  return showBanner ? (
    <CookieBanner
      infoText={formatMessage(messages.info, { acceptCookies: formatMessage(messages.button) })}
      buttonText={formatMessage(messages.button)}
      handleAccept={handleAccept}
      handleClose={handleClose}
      link={link}
    />
  ) : null;
};

export { CookieBannerContainer };
